import React, { useEffect, useMemo, useState } from "react";
import "../../VideoManager.css";

const FILTERS = [
  { id: "none", label: "None" },
  { id: "warm", label: "Warm glow" },
  { id: "cool", label: "Cool steel" },
  { id: "matte", label: "Matte" },
];

const CROPS = [
  { id: "auto", label: "Original" },
  { id: "16:9", label: "16:9" },
  { id: "4:5", label: "4:5" },
  { id: "1:1", label: "Square" },
];

const OVERLAY_POSITIONS = [
  { id: "bottom-left", label: "Bottom left" },
  { id: "bottom-right", label: "Bottom right" },
  { id: "top-left", label: "Top left" },
  { id: "top-right", label: "Top right" },
  { id: "center", label: "Center" },
];

const positionMap = {
  "bottom-left": { bottom: 16, left: 16, textAlign: "left" },
  "bottom-right": { bottom: 16, right: 16, textAlign: "right" },
  "top-left": { top: 16, left: 16, textAlign: "left" },
  "top-right": { top: 16, right: 16, textAlign: "right" },
  center: { top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" },
};

export default function ImageEditingStudio() {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [filter, setFilter] = useState("none");
  const [overlayText, setOverlayText] = useState("GCC Worship");
  const [canvasSize, setCanvasSize] = useState("1280x720");
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [cropMode, setCropMode] = useState(CROPS[0].id);
  const [overlayColor, setOverlayColor] = useState("#ffffff");
  const [overlaySize, setOverlaySize] = useState(32);
  const [borderColor, setBorderColor] = useState("#272a35");
  const [borderWidth, setBorderWidth] = useState(0);
  const [overlayPosition, setOverlayPosition] = useState(OVERLAY_POSITIONS[0].id);
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const parseSize = (value) => {
    const [width, height] = value.split("x").map((val) => Number(val.trim()));
    return {
      width: Number.isNaN(width) ? 1280 : width,
      height: Number.isNaN(height) ? 720 : height,
    };
  };

  const gradientDefinition = useMemo(() => {
    if (filter === "warm") {
      return "linear-gradient(180deg, rgba(255, 165, 0, 0.25), transparent)";
    }
    if (filter === "cool") {
      return "linear-gradient(180deg, rgba(0, 0, 255, 0.25), transparent)";
    }
    if (filter === "matte") {
      return "linear-gradient(180deg, rgba(0, 0, 0, 0.2), rgba(255, 255, 255, 0.15))";
    }
    return "none";
  }, [filter]);

  const ratioPadding = useMemo(() => {
    const ratioMap = {
      "16:9": "56.25%",
      "4:5": "125%",
      "1:1": "100%",
      auto: "67%",
    };
    return ratioMap[cropMode] || ratioMap.auto;
  }, [cropMode]);

  const overlayStyle = useMemo(() => {
    const position = positionMap[overlayPosition] || positionMap["bottom-left"];
    return {
      position: "absolute",
      color: overlayColor,
      fontWeight: 700,
      fontSize: overlaySize,
      textShadow: "0 2px 8px rgba(0,0,0,0.65)",
      ...position,
    };
  }, [overlayColor, overlaySize, overlayPosition]);

  const previewStyle = useMemo(
    () => ({
      backgroundImage: previewUrl ? `url(${previewUrl})` : "none",
      backgroundSize: "cover",
      minHeight: 220,
      position: "relative",
      paddingTop: ratioPadding,
      filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`,
      border: `${borderWidth}px solid ${borderColor}`,
      overflow: "hidden",
    }),
    [previewUrl, ratioPadding, brightness, contrast, saturation, borderWidth, borderColor],
  );

  const createExportDataUrl = async ({ width, height }) => {
    if (!previewUrl) return "";
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, width, height);

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = previewUrl;
    await new Promise((resolve, reject) => {
      if (image.complete) {
        resolve();
        return;
      }
      image.onload = resolve;
      image.onerror = reject;
    });

    const scale = Math.max(width / image.width, height / image.height);
    const drawWidth = image.width * scale;
    const drawHeight = image.height * scale;
    const offsetX = (width - drawWidth) / 2;
    const offsetY = (height - drawHeight) / 2;

    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

    const overlayGradient = (() => {
      if (filter === "warm") {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, "rgba(255, 165, 0, 0.25)");
        gradient.addColorStop(1, "transparent");
        return gradient;
      }
      if (filter === "cool") {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, "rgba(0, 0, 255, 0.25)");
        gradient.addColorStop(1, "transparent");
        return gradient;
      }
      if (filter === "matte") {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, "rgba(0, 0, 0, 0.2)");
        gradient.addColorStop(1, "rgba(255, 255, 255, 0.15)");
        return gradient;
      }
      return null;
    })();
    if (overlayGradient) {
      ctx.fillStyle = overlayGradient;
      ctx.fillRect(0, 0, width, height);
    }

    const overlayPos = positionMap[overlayPosition] || positionMap["bottom-left"];
    ctx.font = `${overlaySize}px "Inter", sans-serif`;
    ctx.fillStyle = overlayColor;
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = overlayPos.textAlign || "left";
    let textX = overlayPos.left || 32;
    if (overlayPos.right) {
      textX = width - 32;
    }
    if (overlayPos.left && overlayPos.right) {
      textX = width / 2;
    }
    let textY = overlayPos.top || height - 32;
    if (overlayPos.bottom) {
      textY = height - 32;
    }
    if (overlayPosition === "center") {
      textY = height / 2 + overlaySize / 3;
    }

    ctx.fillText(overlayText, textX, textY);

    if (borderWidth > 0) {
      ctx.lineWidth = borderWidth;
      ctx.strokeStyle = borderColor;
      ctx.strokeRect(borderWidth / 2, borderWidth / 2, width - borderWidth, height - borderWidth);
    }

    return canvas.toDataURL("image/png");
  };

  const triggerDownload = async (type) => {
    if (!previewUrl) return;
    const size = type === "banner" ? { width: 1920, height: 1080 } : parseSize(canvasSize);
    const dataUrl = await createExportDataUrl(size);
    if (!dataUrl) return;
    const anchor = document.createElement("a");
    const baseName = imageFile ? imageFile.name.replace(/\.[^.]+$/, "") : "gcc-image";
    anchor.href = dataUrl;
    anchor.download = `${baseName}-${type}.png`;
    anchor.click();
  };

  return (
    <section className="studio-panel">
      <div className="studio-panel__header">
        <h2>Image Editing Studio</h2>
        <p>Trades thumbnails, lyric cards, and social overlays with text, filters, and crop settings.</p>
      </div>

      <div className="studio-panel__dual-grid video-studio__layout">
        <div className="studio-panel__controls-column">
          <div className="studio-panel__section-card studio-panel__compact-section">
            <h4>Upload & canvas</h4>
            <input type="file" accept="image/*" onChange={handleImageUpload} />
            <label>
              <span>Canvas size</span>
              <select value={canvasSize} onChange={(event) => setCanvasSize(event.target.value)}>
                <option value="1280x720">1280×720</option>
                <option value="1920x1080">1920×1080</option>
                <option value="1200x1200">1200×1200</option>
              </select>
            </label>
            <label>
              <span>Crop ratio</span>
              <select value={cropMode} onChange={(event) => setCropMode(event.target.value)}>
                {CROPS.map((crop) => (
                  <option key={crop.id} value={crop.id}>
                    {crop.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="studio-panel__section-card studio-panel__compact-section">
            <h4>Filter & tone</h4>
            <div className="studio-panel__row-grid">
              <label>
                <span>Filter</span>
                <select value={filter} onChange={(event) => setFilter(event.target.value)}>
                  {FILTERS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Brightness</span>
                <input
                  type="range"
                  min={70}
                  max={140}
                  value={brightness}
                  onChange={(event) => setBrightness(Number(event.target.value))}
                />
              </label>
              <label>
                <span>Contrast</span>
                <input
                  type="range"
                  min={70}
                  max={160}
                  value={contrast}
                  onChange={(event) => setContrast(Number(event.target.value))}
                />
              </label>
              <label>
                <span>Saturation</span>
                <input
                  type="range"
                  min={70}
                  max={160}
                  value={saturation}
                  onChange={(event) => setSaturation(Number(event.target.value))}
                />
              </label>
            </div>
          </div>

          <div className="studio-panel__section-card studio-panel__compact-section">
            <h4>Overlay text</h4>
            <div className="studio-panel__row-grid">
              <label>
                <span>Text</span>
                <input value={overlayText} onChange={(event) => setOverlayText(event.target.value)} />
              </label>
              <label>
                <span>Text size</span>
                <input
                  type="range"
                  min={24}
                  max={72}
                  value={overlaySize}
                  onChange={(event) => setOverlaySize(Number(event.target.value))}
                />
              </label>
              <label>
                <span>Text color</span>
                <input type="color" value={overlayColor} onChange={(event) => setOverlayColor(event.target.value)} />
              </label>
            </div>
            <div className="studio-panel__options">
              {OVERLAY_POSITIONS.map((option) => (
                <label key={option.id} className="studio-panel__checkbox-label">
                  <input
                    type="radio"
                    name="overlay-position"
                    value={option.id}
                    checked={overlayPosition === option.id}
                    onChange={(event) => setOverlayPosition(event.target.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="studio-panel__section-card studio-panel__compact-section">
            <h4>Border & finish</h4>
            <div className="studio-panel__row-grid">
              <label>
                <span>Border color</span>
                <input type="color" value={borderColor} onChange={(event) => setBorderColor(event.target.value)} />
              </label>
              <label>
                <span>Border width</span>
                <input
                  type="range"
                  min={0}
                  max={20}
                  value={borderWidth}
                  onChange={(event) => setBorderWidth(Number(event.target.value))}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="studio-panel__preview-column">
          <div className="recording-studio__preview-card">
            <div className="recording-studio__label">Preview</div>
            {previewUrl ? (
              <div className="studio-panel__video-preview" style={previewStyle}>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: gradientDefinition,
                    pointerEvents: "none",
                  }}
                />
                <div style={overlayStyle}>{overlayText}</div>
              </div>
            ) : (
              <div className="recording-studio__placeholder">Upload an image to preview your layout.</div>
            )}
            <div className="studio-panel__preview-actions">
              <button
                type="button"
                className="studio-panel__cta"
                disabled={!previewUrl}
                onClick={() => triggerDownload("thumbnail")}
              >
                Download thumbnail
              </button>
              <button
                type="button"
                className="studio-panel__cta studio-panel__cta--secondary"
                disabled={!previewUrl}
                onClick={() => triggerDownload("banner")}
              >
                Export banner
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
